import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
	createBgProcess,
	getAllBgProcess,
	updateBgProcess,
} from '@/services/bgProcess';

export enum BgProcessModule {
	BV = 'Benefits Verification',
	ORDER_REQUEST_SINGLE = 'Order Request (Single)',
	ORDER_REQUEST_BULK = 'Order Request (Bulk)',
	RETURN_REQUEST = 'Return Request',
	SUPPORT_TICKET = 'Support Ticket',
	CONSIGNMENT_ORDER = 'Consignment Order',
	PATIENTS = 'Patients',
	UTILITIES_ASSIGN_GRAFTS = 'Utilities (Assign Grafts)',
	UTILITIES_CONFIRM_GRAFTS = 'Utilities (Confirm Grafts)',
	DENIAL_SUPPORT = 'Denial Support',
}

interface BgMutationArgs {
	moduleName: string;
	userId: string;
	payload: any;
	crmId?: string; // only for PATCH
	clinicId?: string;
	flag?: number;
}

export const useGetBgProcess = ({
	clinicIds,
	enabled = true,
}: {
	clinicIds: string;
	enabled?: boolean;
}) => {
	return useQuery({
		queryKey: ['bg-process', clinicIds],
		queryFn: () => getAllBgProcess({ clinicIds }),
		enabled,
		keepPreviousData: false,
		refetchInterval: 5000,
	});
};

export const useBgProcessMutation = (
	mutationFn: (args: BgMutationArgs) => Promise<any>,
	onSuccess?: (data: any, variables: BgMutationArgs) => void,
	onError?: (error: unknown) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn,
		onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['bg-process'] });
      onSuccess?.(data, variables);
    },
		onError: (error: any) => {
			onError?.(error);
			toast.error(
				error?.response?.data?.error?.message ||
					error?.response?.data?.error?.message?.error?.message ||
					'Failed to process background task. Please try again.',
			);
		},
	});
};

export const useCreateBgProcess = (
	onSuccess?: (data: any, variables: BgMutationArgs) => void,
	onError?: (err: unknown) => void,
) => {
	return useBgProcessMutation(createBgProcess, onSuccess, onError);
};

export const useUpdateBgProcess = (
	onSuccess?: (data: any, variables: BgMutationArgs) => void,
	onError?: (err: unknown) => void,
) => {
	return useBgProcessMutation(updateBgProcess, onSuccess, onError);
};

//Usage for the create and update bg process
// const {
//   mutate: createBgProcess,
//   isLoading,
//   isError,
//   isSuccess,
// } = useCreateBgProcess();

// const handleSubmit = () => {
//   createBgProcess({
//     moduleName: 'products',
//     userId: '123',
//     payload: formData,
//   });
// };
